import { memo } from 'react'
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { CONNECTION_STATUS } from '../../hooks/useChannelWebSocket'
import { trackButtonClick } from '../../utils/analytics'

/**
 * ChannelHeader - Header section for channel view
 * Displays channel name, description, connection status, and needs reply badge
 * 
 * @param {boolean} showChannelsList - Whether sidebar is visible
 * @param {Function} onToggleSidebar - Callback to toggle sidebar
 * @param {Object} activeChannel - Currently active channel
 * @param {Function} getChannelDisplayName - Helper to format channel name
 * @param {boolean} isAuthenticated - Whether user is logged in
 * @param {string} connectionStatus - WebSocket connection status
 * @param {boolean} isAdmin - Whether user is admin
 * @param {number} needsReplyCount - Count of channels needing admin reply
 */
const ChannelHeader = memo(function ChannelHeader({
  activeChannel,
  getChannelDisplayName,
  isAuthenticated,
  connectionStatus
}) {
  const { t } = useTranslation()

  return (
    <div className="flex-shrink-0 border-b border-gray-200/50 px-3 pt-3 pb-2 bg-white/90 backdrop-blur-sm">
      <div className="flex items-center gap-2 min-w-0">
        <ChatBubbleLeftRightIcon className="w-4 h-4 text-primary flex-shrink-0" />
        <h2 className="text-base font-bold text-gray-900 truncate min-w-0 flex-1">
          {activeChannel?.channelType === 'entity' && activeChannel?.entityId ? (
            <a
              href={`/entity/${activeChannel.entityId}`}
              className="hover:text-primary hover:underline transition-colors"
              onClick={(e) => {
                e.preventDefault()
                window.location.href = `/entity/${activeChannel.entityId}`
              }}
            >
              {getChannelDisplayName(activeChannel)}
            </a>
          ) : activeChannel?.channelType === 'default' && activeChannel.ownerEmail && activeChannel.ownerFirstName ? (
            <>
              <span>{`${activeChannel.ownerFirstName} ${activeChannel.ownerLastName}`}</span>
              <span className="ml-1 text-sm font-normal text-gray-500">{activeChannel.ownerEmail}</span>
            </>
          ) : (
            <span>{activeChannel ? getChannelDisplayName(activeChannel) : t('channels.supportChannel')}</span>
          )}
        </h2>
        {/* WebSocket connection status indicator */}
        {isAuthenticated && activeChannel?.id !== 'mock-support' && (
          <span
            className="flex-shrink-0"
            title={
              connectionStatus === CONNECTION_STATUS.CONNECTED ? t('channels.connection.connected') :
              connectionStatus === CONNECTION_STATUS.CONNECTING ? t('channels.connection.connecting') :
              connectionStatus === CONNECTION_STATUS.RECONNECTING ? t('channels.connection.reconnecting') :
              connectionStatus === CONNECTION_STATUS.ERROR ? t('channels.connection.error') :
              t('channels.connection.disconnected')
            }
          >
            {connectionStatus === CONNECTION_STATUS.CONNECTED && <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
            {connectionStatus === CONNECTION_STATUS.CONNECTING && <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />}
            {connectionStatus === CONNECTION_STATUS.RECONNECTING && <span className="inline-block w-2 h-2 bg-orange-500 rounded-full animate-pulse" />}
            {(connectionStatus === CONNECTION_STATUS.DISCONNECTED || connectionStatus === CONNECTION_STATUS.ERROR) && <span className="inline-block w-2 h-2 bg-red-500 rounded-full" />}
          </span>
        )}
      </div>
      {activeChannel?.description && (
        <p className="text-xs text-gray-400 mt-1 ml-6 truncate">
          {activeChannel.description === 'Direct communication with the TrckIt support team'
            ? t('channels.supportChannelDescription')
            : activeChannel.description}
        </p>
      )}
    </div>
  )
})

export default ChannelHeader
